import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Space } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, PhoneOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useCreateUserMutation } from 'src/redux/services/centralApi';

const { Title, Text, } = Typography;

interface IToken {
	org_id?: string;
	partner_id?: string;
	role: string;
	expiration: number;
}

interface ISignUpForm {
	email: string;
	full_name: string;
	phone_number: string;
	password: string;
	confirm_password: string;
}

export default function SignUpPage() {
	const [ form, ] = Form.useForm();
	const [ tokenData, setTokenData, ] = useState<IToken | null>(null);
	const location = useLocation();
	const navigate = useNavigate();

	const [ createUser, createUserState, ] = useCreateUserMutation();

	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const token = urlParams.get('token');

		if (!token) {
			toast.error('Token não fornecido!');
			navigate('/');
			return;
		}

		try {
			const decodedToken = JSON.parse(atob(token));
			if (!decodedToken.role || !decodedToken.expiration || !(decodedToken.org_id || decodedToken.partner_id)) {
				toast.error('Token inválido!');
				navigate('/login');
				return;
			}

			if (
				(decodedToken.role === 'partner'
					|| decodedToken.role === 'admin')
				&& decodedToken.expiration
				&& decodedToken.expiration < Date.now()
			) {
				toast.error('Link de cadastro expirado!');
				navigate('/login');
				return;
			}

			setTokenData(decodedToken);
			console.log('Decoded token data:', decodedToken);
		} catch (error) {
			toast.error('Token inválido!');
			navigate('/login');
		}
	}, [ location.search, navigate, ]);

	const onFinish = async (values: ISignUpForm) => {
		if (!tokenData) {
			toast.error('Dados do token inválidos!');
			return;
		}

		// Ensure role is one of the allowed string literals
		const allowedRoles = [ 'user', 'partner', 'admin', 'super_admin', ] as const;
		type AllowedRole = typeof allowedRoles[number];
		const role: AllowedRole = allowedRoles.includes(tokenData.role as AllowedRole)
			? tokenData.role as AllowedRole
			: 'user';

		try {
			const userData = {
				email: values.email,
				full_name: values.full_name,
				password: values.confirm_password,
				phone_number: values.phone_number ? phoneMaskToRaw(values.phone_number) : undefined,
				notify_email: true,
				role,
				...(tokenData.org_id && { org_id: tokenData.org_id, }),
				...(tokenData.partner_id && { partner_id: tokenData.partner_id, }),
			};

			await createUser({ user: userData, })
				.then(() => {
					toast.success('Usuário criado com sucesso!');
					form.resetFields();

					navigate('/login');
				}).catch(() => {
					toast.error('Erro ao criar usuário!');
				});
		} catch (error) {
			console.error(error);
			toast.error('Erro ao criar usuário!');
		}
	};

	const onFinishFailed = () => {
		toast.error('Por favor, corrija os erros no formulário!');
	};


	const phoneMask = (value: string) => {
		let phoneNumber = value?.replace(/\D/g, '');
	
		if (phoneNumber.length > 13) {
			phoneNumber = phoneNumber.substring(0, 13);
		}
	
		phoneNumber = phoneNumber
			.replace(/^(\d{2})(\d{2})(\d)/, '$1 ($2) $3')
			.replace(/(\d)(\d{4})$/, '$1-$2');
	
		return phoneNumber;
	};
	
	const phoneMaskToRaw = (value: string) => {
		const rawPhone = value.replace(/\D/g, '');
		return rawPhone;
	};
	
	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const maskedValue = phoneMask(value);
		form.setFieldsValue({ phone_number: maskedValue, });
		form.validateFields();
	};

	if (!tokenData) {
		return (
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: '100vh',
			}}
			>
				<Text>Carregando...</Text>
			</div>
		);
	}

	return (
		<div style={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			minHeight: '100vh',
			backgroundColor: '#f5f5f5',
		}}
		>
			<Card style={{ width: 400, boxShadow: '0 4px 8px rgba(0,0,0,0.1)', }}>
				<Space direction="vertical" size="small" style={{ width: '100%', }}>
					<div style={{ textAlign: 'center', }}>
						<Title level={3}>
							Criar Conta
							{tokenData.role === 'partner' ?
								' de Parceiro' : tokenData.role === 'admin' ?
									' de Administrador' : ''}
						</Title>
					</div>

					<Form
						form={form}
						name="signup"
						onFinish={onFinish}
						onFinishFailed={onFinishFailed}
						layout="vertical"
						requiredMark={false}
					>
						<Form.Item
							name="full_name"
							label="Nome Completo"
							rules={[
								{ required: true, message: 'Por favor, insira seu nome completo!', },
								{ min: 2, message: 'Nome deve ter pelo menos 2 caracteres!', },
							]}
						>
							<Input
								prefix={<UserOutlined />}
								placeholder="Digite seu nome completo"
								size="large"
							/>
						</Form.Item>
						{
							tokenData.role !== 'partner' && (
								<Form.Item
									name="phone_number"
									label="Telefone"
									rules={[
										{
											required: true,
											message: 'Informe o telefone',
										},
										{
											validator: (_, value) => {
												if (value.match(/^55 \(\d{2}\) \d{5}-\d{4}$/)) {
													return Promise.resolve();
												}
												return Promise.reject('Insira um número válido.');
											},
											message: 'Insira um número válido.',
										},
									]}
								>
									<Input
										prefix={<PhoneOutlined />}
										placeholder="55 (99) 98888-8888"
										onChange={handlePhoneChange}
									/>
								</Form.Item>
							)
						}
						<Form.Item
							name="email"
							label="E-mail"
							rules={[
								{ required: true, message: 'Por favor, insira seu e-mail!', },
								{ type: 'email', message: 'E-mail inválido!', },
							]}
						>
							<Input
								prefix={<MailOutlined />}
								placeholder="Digite seu e-mail"
								size="large"
							/>
						</Form.Item>

						<Form.Item
							name="password"
							label="Senha"
							rules={[
								{ required: true, message: 'Por favor, insira sua senha!', },
								{ min: 6, message: 'Senha deve ter pelo menos 6 caracteres!', },
							]}
						>
							<Input.Password
								prefix={<LockOutlined />}
								placeholder="Digite sua senha"
								size="large"
							/>
						</Form.Item>

						<Form.Item
							name="confirm_password"
							label="Confirmar Senha"
							dependencies={[ 'password', ]}
							rules={[
								{ required: true, message: 'Por favor, confirme sua senha!', },
								({ getFieldValue, }) => ({
									validator(_, value) {
										if (!value || getFieldValue('password') === value) {
											return Promise.resolve();
										}
										return Promise.reject(new Error('As senhas não coincidem!'));
									},
								}),
							]}
						>
							<Input.Password
								prefix={<LockOutlined />}
								placeholder="Confirme sua senha"
								size="large"
							/>
						</Form.Item>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								loading={createUserState.isLoading}
								size="large"
								style={{ width: '100%', }}
							>
								Cadastre-se
							</Button>
						</Form.Item>
					</Form>
				</Space>
			</Card>
		</div>
	);
}